import { useState, useEffect } from 'react';
import { SlideOver } from '@/components/SlideOver';
import { Button } from '@/components/Button';
import { Search, Plus } from 'lucide-react';
import { useCollectionTree, useAddModelsToCollection } from '../hooks';
import { CollectionTree } from './CollectionTree';
import { CreateCollectionForm } from './CreateCollectionForm';
import { useUIStore } from '@/stores/ui';

interface AddToCollectionSlideOverProps {
  open: boolean;
  onClose: () => void;
  modelIds: string[]; // Can add single or multiple models
  currentCollectionIds?: string[]; // Collections the model(s) are already in
}

export function AddToCollectionSlideOver({
  open,
  onClose,
  modelIds,
  currentCollectionIds = [],
}: AddToCollectionSlideOverProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCollectionIds, setSelectedCollectionIds] =
    useState<string[]>(currentCollectionIds);
  const [showCreateCollection, setShowCreateCollection] = useState(false);
  const [newCollectionId, setNewCollectionId] = useState<string | null>(null);

  const { data: collectionTree, isLoading } = useCollectionTree();
  const addModels = useAddModelsToCollection();
  const { addToast } = useUIStore();

  // When a new collection is created, select it automatically
  useEffect(() => {
    if (newCollectionId && !selectedCollectionIds.includes(newCollectionId)) {
      setSelectedCollectionIds([...selectedCollectionIds, newCollectionId]);
      setNewCollectionId(null);
    }
  }, [newCollectionId, selectedCollectionIds]);

  const handleToggleSelection = (collectionId: string) => {
    if (selectedCollectionIds.includes(collectionId)) {
      setSelectedCollectionIds(selectedCollectionIds.filter((id) => id !== collectionId));
    } else {
      setSelectedCollectionIds([...selectedCollectionIds, collectionId]);
    }
  };

  const handleSave = async () => {
    // Find collections to add to (newly selected)
    const collectionsToAddTo = selectedCollectionIds.filter(
      (id) => !currentCollectionIds.includes(id)
    );

    if (collectionsToAddTo.length === 0) {
      addToast({
        type: 'info',
        title: 'No changes',
        message: 'No new collections selected',
      });
      onClose();
      return;
    }

    try {
      // Add to each collection
      await Promise.all(
        collectionsToAddTo.map((collectionId) =>
          addModels.mutateAsync({
            collectionId,
            input: { modelIds },
          })
        )
      );

      addToast({
        type: 'success',
        title: 'Added to collections',
        message: `Added ${modelIds.length} model(s) to ${collectionsToAddTo.length} collection(s)`,
      });

      onClose();
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Failed to add to collections',
        message: error instanceof Error ? error.message : 'An error occurred',
      });
    }
  };

  const handleCreateNew = () => {
    // Open nested slideover to create collection
    setShowCreateCollection(true);
  };

  const handleCollectionCreated = (createdCollectionId: string) => {
    // Close the create collection slideover
    setShowCreateCollection(false);
    // Set the new collection ID to trigger auto-selection
    setNewCollectionId(createdCollectionId);
  };

  // Filter collections based on search
  const filterTree = (collections: any[], query: string): any[] => {
    if (!query) return collections;

    return collections
      .map((collection) => {
        const matchesSearch = collection.name.toLowerCase().includes(query.toLowerCase());
        const filteredChildren = filterTree(collection.children || [], query);

        if (matchesSearch || filteredChildren.length > 0) {
          return {
            ...collection,
            children: filteredChildren,
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const filteredTree = filterTree(collectionTree || [], searchQuery);
  const hasChanges = selectedCollectionIds.some((id) => !currentCollectionIds.includes(id));

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Add to Collections"
      description="Select collections to add this model to"
    >
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search collections..."
            className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        {/* New Collection Button */}
        <button
          onClick={handleCreateNew}
          className="w-full flex items-center gap-2 py-2 px-3 border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 rounded-md transition-colors text-sm font-medium text-foreground"
        >
          <Plus className="h-4 w-4" />
          <span>Create New Collection</span>
        </button>

        {/* Collection Tree with Checkboxes */}
        <div className="border border-border rounded-md p-3 max-h-96 overflow-y-auto">
          <CollectionTree
            collections={filteredTree}
            isLoading={isLoading}
            selectedCollectionIds={selectedCollectionIds}
            onSelect={handleToggleSelection}
            showCheckbox={true}
            emptyMessage={
              searchQuery
                ? `No collections found for "${searchQuery}"`
                : 'Create your first collection'
            }
          />
        </div>

        {/* Info Text */}
        <p className="text-xs text-foreground-muted">
          {selectedCollectionIds.length > 0
            ? `${selectedCollectionIds.length} collection(s) selected`
            : 'Select collections to add your model(s) to'}
        </p>

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasChanges || addModels.isPending}
          >
            {addModels.isPending ? 'Adding...' : 'Add to Collections'}
          </Button>
        </div>
      </div>

      {/* Nested SlideOver for Creating Collection */}
      <SlideOver
        open={showCreateCollection}
        onClose={() => setShowCreateCollection(false)}
        title="Create Collection"
        description="Create a new collection to organize your models"
      >
        <CreateCollectionForm
          onSuccess={(createdCollection) => {
            if (createdCollection?.id) {
              handleCollectionCreated(createdCollection.id);
            }
          }}
          onCancel={() => setShowCreateCollection(false)}
        />
      </SlideOver>
    </SlideOver>
  );
}
